/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useInfiniteQuery, useQuery, useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { addDays } from 'date-fns';
import {
  useAccessToken,
  useIsLoggedIn,
  useUser,
} from '@airasia-rn/account/hooks';
import { useAppConfig } from '@airasia-rn/ui';
import { SnapStaticApiService } from '../../services/SnapStaticApiService';
import type { SnapLocationFilterItem } from '../../services/SnapStaticApiService';
import { SnapWebApiService } from '../../services/SnapWebApiService';
import { SnapRecommendationApiService } from '../../services/SnapRecommendationApiService';
import { SnapSmartFillApiService } from '../../services/SnapSmartFillApiService';
import { createJwtTokenProvider } from '@airasia-rn/hotels/services/auth';
import { useSnapConfig } from '../../configs/SnapConfigProvider';
import {
  SNAP_LFC_AIRLINE_PROFILE,
  MAX_DATE_OFFSET_DAYS,
  MIN_STATION_CODE_LENGTH,
  SNAP_LFC_DIRECTIONS,
  SNAP_USER_TYPE,
} from '../../configs/constants';
import { useSnapSearchViewFeatureFlag } from '../useSnapSearchViewFeatureFlag';
import {
  filterDestinationsForSnap,
  resolveDestinationLookupStationCode,
} from '../../utils/stationUtils';
import { getActiveDateChunks } from '../../utils/lfcUtils';
import type { BannerApiResponse } from '../../types/banner';
import type {
  LfcResponse,
  LfcFlightData,
  LfcChunkResult,
  SnapListingParams,
  UseSnapLfcParams,
  UseSnapLfcResult,
} from '../../types';
import type { SnapListingFlights } from '../../types/snapRooms';
import { hasMandatorySnapListingParams } from '../../utils/snapListingContext';
import { shareSnapListingInflight } from '../../utils/snapListingInflight';
import { snapKeys } from './keys';
import { useSnapListingQueryGate } from './useSnapListingQueryGate';

export function useSnapApiService(): SnapStaticApiService {
  const { config: appConfig } = useAppConfig();
  return useMemo(
    () => new SnapStaticApiService(appConfig.env),
    [appConfig.env],
  );
}

export function useSnapLfcApiService(): SnapWebApiService {
  const { config: appConfig } = useAppConfig();
  const { config: snapConfig } = useSnapConfig();
  const lfcChannelHash = snapConfig.lfcChannelHash;

  return useMemo(
    () => new SnapWebApiService(appConfig.env, lfcChannelHash),
    [appConfig.env, lfcChannelHash],
  );
}

export function useSnapListingApiService(): SnapWebApiService {
  const { config: appConfig } = useAppConfig();
  const { config: snapConfig } = useSnapConfig();
  const lfcChannelHash = snapConfig.lfcChannelHash;

  return useMemo(
    () => new SnapWebApiService(appConfig.env, lfcChannelHash),
    [appConfig.env, lfcChannelHash],
  );
}

type SnapHotelSearchPageParam = {
  pageNumber: number;
  snapId?: string;
  airlineProfile?: string;
  departFlightId?: string;
  returnFlightId?: string;
};

const getValidHotelPageSize = (value: number | undefined) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }

  return value;
};

export function useSnapHotelSearch(
  params: SnapListingParams,
  options: { enabled?: boolean } = {},
) {
  const api = useSnapListingApiService();
  const { config: appConfig } = useAppConfig();
  const { hotel_list_default_page_size, isSettingsLoading } =
    useSnapSearchViewFeatureFlag();
  const accessTokenQuery = useAccessToken('SNAP');
  const { isLoggedIn, isLoading: isLoginStateLoading } = useIsLoggedIn('SNAP');
  const userQuery = useUser('SNAP');
  const defaultHotelSize = getValidHotelPageSize(hotel_list_default_page_size);
  const requestParams = useMemo(
    () => ({
      ...params,
      country_code: params.country_code || appConfig.geoId || 'MY',
      hotelSize: params.hotelSize ?? defaultHotelSize,
      Usermailid:
        params.Usermailid || userQuery.data?.email || userQuery.data?.username,
      tier: params.tier || userQuery.data?.loyaltyTier,
      gaid: params.gaid || appConfig.gaId,
    }),
    [
      appConfig.gaId,
      defaultHotelSize,
      params,
      userQuery.data?.email,
      userQuery.data?.loyaltyTier,
      userQuery.data?.username,
    ],
  );
  const hasMandatoryParams = hasMandatorySnapListingParams(
    requestParams as Record<string, string | number | boolean | undefined>,
  );
  const { isEnabled } = useSnapListingQueryGate({
    scope: 'hotels-list',
    paramsLanguage: requestParams.language,
    hasMandatoryParams,
    requireClient: true,
  });
  const shouldWaitForUserProfile = isLoggedIn && userQuery.isLoading;
  const shouldWaitForListingBootstrap =
    isSettingsLoading || isLoginStateLoading || shouldWaitForUserProfile;

  return useInfiniteQuery({
    queryKey: snapKeys.search.listing.params(requestParams),
    queryFn: ({ pageParam = { pageNumber: 0 }, signal }) => {
      const nextPageParam = pageParam as SnapHotelSearchPageParam;
      const isFirstPage = nextPageParam.pageNumber <= 0;
      const pageRequestParams = {
        ...requestParams,
        snapId: nextPageParam.snapId || requestParams.snapId,
        airlineProfile:
          nextPageParam.airlineProfile || requestParams.airlineProfile,
        departFlightId:
          nextPageParam.departFlightId || requestParams.departFlightId,
        returnFlightId:
          nextPageParam.returnFlightId || requestParams.returnFlightId,
        ...(!isFirstPage && { page_number: nextPageParam.pageNumber }),
      };
      const inflightKey = JSON.stringify([
        ...snapKeys.search.listing.params(pageRequestParams),
        nextPageParam.pageNumber,
        nextPageParam.snapId,
        nextPageParam.airlineProfile,
        nextPageParam.departFlightId,
        nextPageParam.returnFlightId,
      ]);

      return shareSnapListingInflight(
        inflightKey,
        async (abortSignal) => {
          const accessToken = await accessTokenQuery
            .getToken()
            .catch(() => undefined);

          return api.searchSnapHotels(
            pageRequestParams,
            accessToken,
            undefined,
            abortSignal,
          );
        },
        signal,
      );
    },
    enabled:
      (options.enabled ?? true) && isEnabled && !shouldWaitForListingBootstrap,
    initialPageParam: { pageNumber: 0 } satisfies SnapHotelSearchPageParam,
    getNextPageParam: (lastPage, allPages) => {
      const hotels = Array.isArray(lastPage?.hotels) ? lastPage.hotels : [];
      const hasMoreHotels =
        lastPage?.hasMoreHotels ?? lastPage?.has_more_hotels ?? false;
      const flights = (lastPage as { flights?: SnapListingFlights })?.flights;

      if (!hasMoreHotels || hotels.length === 0) {
        return undefined;
      }

      return {
        pageNumber:
          typeof lastPage?.pageNumber === 'number'
            ? lastPage.pageNumber + 1
            : allPages.length,
        snapId: lastPage?.snapId || requestParams.snapId,
        airlineProfile: requestParams.airlineProfile || flights?.airlineProfile,
        departFlightId:
          requestParams.departFlightId || flights?.departFlight?.journeysellkey,
        returnFlightId:
          requestParams.returnFlightId || flights?.returnFlight?.journeysellkey,
      } satisfies SnapHotelSearchPageParam;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function useSnapHotelRecommendations(
  params: SnapListingParams,
  options: { enabled?: boolean } = {},
) {
  const { config } = useAppConfig();
  const accessTokenQuery = useAccessToken('SNAP');
  const api = useMemo(
    () => new SnapRecommendationApiService(config.env),
    [config.env],
  );
  const { isEnabled } = useSnapListingQueryGate({
    scope: 'recommended-list',
    paramsLanguage: params.language,
    hasMandatoryParams: Boolean(
      params.orig &&
        params.dest &&
        params.currency &&
        params.language &&
        params.airlineProfile &&
        params.departFlightId,
    ),
  });

  return useQuery({
    queryKey: snapKeys.search.recommendations.params(params),
    queryFn: async ({ signal }) => {
      const inflightKey = JSON.stringify(
        snapKeys.search.recommendations.params(params),
      );

      return shareSnapListingInflight(
        inflightKey,
        async (abortSignal) => {
          const accessToken = await accessTokenQuery
            .getToken()
            .catch(() => undefined);

          return api.fetchHotelRecommendations(
            {
              ...params,
              country_code: params.country_code || config.geoId || 'MY',
              currency: params.currency || config.currency || 'MYR',
              language: params.language || config.locale || 'en-gb',
            },
            accessToken,
            abortSignal,
          );
        },
        signal,
      );
    },
    enabled: isEnabled && (options.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function useSnapBanners() {
  const api = useSnapApiService();
  const { config } = useAppConfig();

  return useQuery<BannerApiResponse>({
    queryKey: snapKeys.search.banner.env(config.env),
    queryFn: () => api.fetchSnapBanners(),
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useSnapLocationFilters(location: string, language: string) {
  const api = useSnapApiService();
  const { config: appConfig } = useAppConfig();
  const normalizedLocation = location.trim();
  const normalizedLanguage = language.trim() || 'en-gb';

  return useQuery<SnapLocationFilterItem[]>({
    queryKey: snapKeys.search.locationFilter.params(
      normalizedLocation,
      normalizedLanguage,
      appConfig.env,
    ),
    queryFn: async () => {
      const response = await api.getLocationFilters(
        normalizedLocation,
        normalizedLanguage,
      );

      return response.location?.[0]?.data ?? [];
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: normalizedLocation.length > 0,
  });
}

/** Fetches SNAP's airport SmartFill data for listing filters. */
export function useSnapSmartFillApi(locationId: string) {
  const { config: appConfig } = useAppConfig();
  const { config: snapConfig } = useSnapConfig();
  const api = useMemo(
    () => new SnapSmartFillApiService(appConfig.env),
    [appConfig.env],
  );
  const normalizedLocation = locationId.trim();
  const language = appConfig.locale || 'en-gb';
  const tokenProvider = useMemo(
    () =>
      createJwtTokenProvider(
        appConfig.env,
        snapConfig.hotelJwtHash || '',
        snapConfig.hotelXorKey || '',
      ),
    [appConfig.env, snapConfig.hotelJwtHash, snapConfig.hotelXorKey],
  );
  return useQuery({
    queryKey: snapKeys.search.smartFill.params(
      normalizedLocation,
      language,
      appConfig.env,
    ),
    queryFn: async () => {
      const token = await tokenProvider.getToken();
      return api.fetchSmartfill(
        normalizedLocation,
        token || '',
        snapConfig.hotelClientId || '',
        language,
      );
    },
    enabled:
      Boolean(normalizedLocation) &&
      Boolean(snapConfig.hotelClientId) &&
      Boolean(snapConfig.hotelJwtHash) &&
      Boolean(snapConfig.hotelXorKey),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 2,
  });
}

/**
 * Hook to fetch valid destinations for a given station
 * Filters destinations to only include those supporting SNAP (dotrez inventory)
 * Refetches only when origin station changes
 *
 * When snap_bkk_dmk_mapping_enabled is on, the lookup station is remapped
 * (BKK -> DMK) so Bangkok origins get AirAsia's destination file. The caller's
 * selected origin is unaffected — only the fetched file changes.
 *
 * @param stationCode - Origin station code (e.g., 'PEN', 'KUL')
 * @returns Query result with filtered destination codes
 *
 * @example
 * ```tsx
 * const { data: validDestinations, isLoading } = useDestinations('PEN');
 * // Returns ['KUL', 'SIN', 'TPE'] etc.
 * ```
 */
export function useDestinations(stationCode: string) {
  const api = useSnapApiService();
  const { config: appConfig } = useAppConfig();
  const { snap_bkk_dmk_mapping_enabled } = useSnapSearchViewFeatureFlag();

  const lookupStationCode = resolveDestinationLookupStationCode(
    stationCode,
    snap_bkk_dmk_mapping_enabled,
  );

  return useQuery({
    queryKey: [
      ...snapKeys.search.destinations.station(lookupStationCode),
      appConfig.env,
    ],
    queryFn: async () => {
      const destinationMap = await api.getDestinations(lookupStationCode);
      const allDestinations = Object.values(destinationMap).reduce(
        (acc, group) => ({ ...acc, ...group }),
        {},
      );
      return filterDestinationsForSnap(allDestinations);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: !!stationCode && stationCode.length >= MIN_STATION_CODE_LENGTH,
  });
}

export function useSnapLfc({
  originCode,
  destinationCode,
  currency,
  tripType,
  userType,
  visibleMonths = [],
}: UseSnapLfcParams): UseSnapLfcResult {
  const lfcApi = useSnapLfcApiService();
  const { isLoggedIn } = useIsLoggedIn('SNAP');
  const { snap_lfc_enabled } = useSnapSearchViewFeatureFlag();
  const resolvedUserType =
    userType ?? (isLoggedIn ? SNAP_USER_TYPE.MEMBER : SNAP_USER_TYPE.ANONYMOUS);

  const hasRouteData =
    !!originCode &&
    !!destinationCode &&
    originCode.length >= MIN_STATION_CODE_LENGTH &&
    destinationCode.length >= MIN_STATION_CODE_LENGTH;

  const isEnabled = snap_lfc_enabled && hasRouteData;
  const isRoundTrip = tripType === 'round-trip';
  const routeKey = `${originCode}-${destinationCode}-${currency}-${tripType}`;
  const requestedVisibleRangeRef = useRef<{ start: Date; end: Date } | null>(
    null,
  );
  const requestedVisibleRouteKeyRef = useRef(routeKey);

  // Memoize maxDate so dateChunks doesn't recalculate every render
  // (addDays(new Date(), N) creates a new object each time, breaking useMemo's Object.is check)
  const maxDate = useMemo(() => addDays(new Date(), MAX_DATE_OFFSET_DAYS), []);

  const requestedVisibleRange = useMemo(() => {
    if (!isEnabled || visibleMonths.length === 0) return null;

    const currentRange = visibleMonths.reduce(
      (acc, [start, end]) => ({
        start: start < acc.start ? start : acc.start,
        end: end > acc.end ? end : acc.end,
      }),
      {
        start: visibleMonths[0][0],
        end: visibleMonths[0][1],
      },
    );

    const previousRange =
      requestedVisibleRouteKeyRef.current === routeKey
        ? requestedVisibleRangeRef.current
        : null;

    if (!previousRange) {
      return currentRange;
    }

    return {
      start:
        currentRange.start < previousRange.start
          ? currentRange.start
          : previousRange.start,
      end:
        currentRange.end > previousRange.end
          ? currentRange.end
          : previousRange.end,
    };
  }, [isEnabled, routeKey, visibleMonths]);

  useEffect(() => {
    requestedVisibleRouteKeyRef.current = routeKey;
    requestedVisibleRangeRef.current = requestedVisibleRange;
  }, [requestedVisibleRange, routeKey]);

  // Convert Date[][] into 3-month API request chunks
  const dateChunks = useMemo(() => {
    if (!requestedVisibleRange) return [];

    return getActiveDateChunks(
      [[requestedVisibleRange.start, requestedVisibleRange.end]],
      3,
      new Date(),
      maxDate,
    );
  }, [requestedVisibleRange, maxDate]);

  const createLfcQuery = (
    {
      origin,
      destination,
      direction,
      chunk,
    }: {
      origin: string;
      destination: string;
      direction: string;
      chunk: { beginDate: string; endDate: string };
    },
    enabled: boolean,
  ) => {
    const queryKey = [
      ...snapKeys.search.lfc.route(origin, destination, currency),
      direction,
      chunk.beginDate,
      chunk.endDate,
    ] as const;

    return {
      queryKey,
      queryFn: () =>
        lfcApi.fetchLowFareCalendar({
          departStation: origin.toUpperCase(),
          arrivalStation: destination.toUpperCase(),
          beginDate: chunk.beginDate,
          endDate: chunk.endDate,
          currency: currency.toUpperCase(),
          airlineProfile: SNAP_LFC_AIRLINE_PROFILE,
          userType: resolvedUserType,
        }),
      select: (response: LfcResponse) => response?.data ?? [],
      enabled,
      staleTime: 0,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnMount: 'always' as const,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    };
  };

  const departQueries = useQueries({
    queries: dateChunks.map((chunk) =>
      createLfcQuery(
        {
          origin: originCode,
          destination: destinationCode,
          direction: SNAP_LFC_DIRECTIONS.DEPART,
          chunk,
        },
        isEnabled,
      ),
    ),
  });

  const returnQueries = useQueries({
    queries: dateChunks.map((chunk) =>
      createLfcQuery(
        {
          origin: destinationCode,
          destination: originCode,
          direction: SNAP_LFC_DIRECTIONS.RETURN,
          chunk,
        },
        isEnabled && isRoundTrip,
      ),
    ),
  });

  const departChunks: LfcChunkResult[] = useMemo(
    () =>
      dateChunks.map((chunk, index) => {
        const query = departQueries[index];
        return {
          chunk,
          data: (query?.data ?? []) as LfcFlightData[],
          isError: !!query?.isError,
          isFetched: !!query?.isFetched,
          isLoading: !!query?.isLoading,
        };
      }),
    [dateChunks, departQueries],
  );

  const returnChunks: LfcChunkResult[] = useMemo(
    () =>
      dateChunks.map((chunk, index) => {
        const query = returnQueries[index];
        return {
          chunk,
          data: (query?.data ?? []) as LfcFlightData[],
          isError: !!query?.isError,
          isFetched: !!query?.isFetched,
          isLoading: !!query?.isLoading,
        };
      }),
    [dateChunks, returnQueries],
  );

  const isLoadingDepart = departQueries.some((q) => q.isLoading);
  const isLoadingReturn = isRoundTrip && returnQueries.some((q) => q.isLoading);
  const isErrorDepart = departQueries.some((q) => q.isError);
  const isErrorReturn = isRoundTrip && returnQueries.some((q) => q.isError);

  return {
    departChunks,
    returnChunks,
    isLoading: isLoadingDepart || isLoadingReturn,
    isError: isErrorDepart || isErrorReturn,
  };
}
